import React, { useState, useMemo } from "react";
import { 
  Box, 
  HStack, 
  Tag, 
  TagLabel, 
  Text, 
  IconButton, 
  Tooltip, 
  MenuList, 
  Menu, 
  MenuItem, 
  MenuButton, 
  Icon,
  Collapse
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { ColumnType } from "build-ts/enums";
import { ErrorDetailProto, FilterProto, MetricProto } from "build-ts/metadata";
import DataTableMKG from "components/analyse/data_table/DataTableMKG";
import { NodeInsights } from "containers/Interfaces";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import { FaChevronDown, FaChevronRight, FaDownload, FaFilter, FaTimes } from "react-icons/fa";
import { IoCopyOutline } from "react-icons/io5";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterName } from "utils/nameUtils";
import { buildActualsMap, copyRawTableToClipboard, downloadRawTableAsExcel, extractRawDataFromNode, getNonMeasureData, isColumnMissing } from "components/analyse/data_table/dataTableUtils";
import { BsFillInfoSquareFill } from "react-icons/bs";
import { useUser } from "context/UserContext";

interface DataTableComponentProps {
  name?: string;
  observationData: NodeInsights;
  metrics?: Map<string, MetricProto>;
  isSelected?: boolean;
  error?: ErrorDetailProto[];

  // close function for table
  onCloseTable?: () => void;
}

const DataTableComponent: React.FC<DataTableComponentProps> = ({ 
  name,
  observationData,
  metrics, 
  isSelected,
  onCloseTable,
  error
}) => { 
  const { configurator, setObservationData } = useAnalysisPlanContext();
  const { defaultTimeZone, quarterStartMonth } = useUser();
  const actualsMap =  buildActualsMap(observationData.current_node?.kg_observation_data!);
  const { nonMeasureColumns } = getNonMeasureData(observationData, defaultTimeZone, quarterStartMonth);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);

  let noOfRows = 0;
  if(nonMeasureColumns.length === 0) {
    noOfRows = Object.keys(actualsMap).length;
  }
  else {
    noOfRows = actualsMap[Object.keys(actualsMap)[0]]?.actual_value?.length || 0;
  }

  // Function to close the table
  const handleCloseTable = () => {
    if (onCloseTable) {
      // Use the custom close function if provided (e.g., from ChatPage)
      onCloseTable();
    } else {
      // Default behavior for AnalysisPlan
      setObservationData(null);
      configurator.onClose();
    }
  };

  // Check if there are any filters to display
  const hasFilters = useMemo(() => {
    let filters: FilterProto[] = [];
    if (observationData.current_node?.kg_observation_data?.kg_observation) {
      filters = observationData.current_node.kg_observation_data?.kg_observation?.filters || [];
    }
    return filters.length > 0;
  }, [observationData]);

  // function to render the filters
  const renderFilters = () => {

    let filters: FilterProto[] = [];
  
    if (observationData.current_node?.observation?.content) {
      filters = observationData.current_node.observation.content.filters || [];
    } 
    // If not, check if node.current_node.kg_observation is present
    else if (observationData.current_node?.kg_observation_data?.kg_observation) {
      filters = observationData.current_node.kg_observation_data?.kg_observation?.filters || [];
    }

    if(filters?.length === 0 || !filters) return null;

    const groupedFilters = filters.reduce((acc, filter) => {
      let colunName = "";
      if(filter?.logical_column_header?.name) {
        colunName = filter?.logical_column_header?.name;
      } else if (filter?.kg_column_header?.name) {
        colunName = filter?.kg_column_header?.name;
      }
      let key = "";
      if(filter?.logical_column_header?.column_type === ColumnType.TEMPORAL) {
        key = "DATE_FILTERS";
      } else {
        key = colunName;
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(filter);
      return acc;
    }, {} as Record<string, FilterProto[]>);
    
  
    return (
      <Box
        overflowX="auto"
        overflowY="auto"
        maxH="95px"
        mb={0.5}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          py={1}
          alignContent="flex-start"
        >
          {Object.entries(groupedFilters).map(([key, filtersGroup], index) => {
            // Special handling for date filters
            if (key === "DATE_FILTERS") {
              return (
                <Tag
                  key={index}
                  size="sm"
                  borderRadius="md"
                  variant="solid"
                  bg="gray.50"
                  color="gray.800"
                  borderWidth="1px"
                  borderColor="gray.300"
                  p={1}
                  minW="fit-content"
                >
                  <TagLabel 
                    fontSize="sm"
                    textAlign="left"
                    whiteSpace="normal"
                  >
                    <Box as="span" fontWeight="700">
                      Date
                    </Box>
                    <Box as="span" fontWeight="normal">
                      {filtersGroup.map((filter, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && " | "}
                          {" "}
                          {getOperatorSymbol(filter?.operator!)}
                          {" "}
                          {filter?.values?.length > 0 
                            ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                            : filter?.date_filter?.absolute_date_filter ? getDateFilterName(filter?.date_filter?.absolute_date_filter!) : getDateFilterName(filter?.date_filter!)}
                        </React.Fragment>
                      ))}
                    </Box>
                  </TagLabel> 
                </Tag>
              );
            }
            
            // Regular handling for non-date filters
            return filtersGroup.map((filter, i) => (
              <Tag
                key={`${index}-${i}`}
                size="sm"
                borderRadius="md"
                variant="solid"
                bg="gray.50"
                color="gray.800"
                borderWidth="1px"
                borderColor="gray.300"
                p={1}
                minW="fit-content"
              >
                <TagLabel 
                  fontSize="sm"
                  textAlign="left"
                  whiteSpace="normal"
                >
                  <Box as="span" fontWeight="700">
                    {filter?.kg_column_header?.name}
                  </Box>
                  <Box as="span" fontWeight="normal">
                    {" "}
                    {getOperatorSymbol(filter?.operator!)}
                    {" "}
                    {filter?.values?.length > 0 
                      ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                      : filter?.date_filter?.absolute_date_filter ? getDateFilterName(filter?.date_filter?.absolute_date_filter!) : getDateFilterName(filter?.date_filter!)}
                  </Box>
                </TagLabel> 
              </Tag>
            ));
          })}
        </Box>
      </Box>
    );
  };
  
  return (
    <Box
      bg="transparent"
      position="relative"
      overflow="hidden"
      p={0}
      width="100%"
      height="auto"
      borderWidth={1}
      borderColor={isSelected ? "purple.700" : "gray.300"} // Add a highlighted border when this node is selected
      borderRadius="md"
      transition="all 0.2s ease-in-out"
    >
      {/* Header Section with Name and Filter Toggle */}
      <Box
        w="100%"
        borderBottomWidth={1}
        borderColor="gray.200"
        bg="transparent"
      >
        <HStack 
          justify="space-between" 
          align="center" 
          w="100%" 
          px={2}
          spacing={2}
          _hover={{ bg: "gray.50" }}
        >
          {/* Name and Filter Icon */}
          <HStack spacing={2} flex={1} minWidth={0}>
            {name && (
              <Text 
                fontSize="sm" 
                fontWeight="600" 
                color="gray.700" 
                m={0}
                isTruncated
              >
                {name}
              </Text>
            )}
            
            {/* Filter Toggle Icon */}
            {hasFilters && (
              <IconButton
                icon={
                  <HStack spacing={0.5}>
                    <Icon as={FaFilter} boxSize={3} />
                    <Icon as={isFiltersExpanded ? FaChevronDown : FaChevronRight} boxSize={2.5} />
                  </HStack>
                }
                aria-label="Toggle filters"
                size="xs"
                variant="ghost"
                color="purple.600"
                _hover={{ bg: "purple.50" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFiltersExpanded(!isFiltersExpanded);
                }}
              />
            )}
          </HStack>
          
          {/* Action Buttons */}
          <HStack spacing={0} flexShrink={0} onClick={(e) => e.stopPropagation()}>
            {noOfRows && noOfRows > 0 && (
              <Tooltip
                label={`${noOfRows} rows present`}
                placement="left"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                p={2}
                borderRadius="md"
                maxWidth="200px"
              >
                <IconButton
                  size="xl"
                  variant="ghost"
                  colorScheme="gray"
                  aria-label="No of rows present"
                  icon={< Icon as={BsFillInfoSquareFill} boxSize={5} color="gray.500" />}
                  _hover={{ bg: "gray.100" }}
                />
              </Tooltip>
            )}
            {error && error.length > 0 && (
              <Tooltip
                label={
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Errors Details:</Text>
                    <ul style={{ paddingLeft: "16px" }}>
                      {error.map((error: ErrorDetailProto, index: number) => (
                        <li key={index}>{error.error_msg}</li>
                      ))}
                    </ul>
                  </Box>
                }
                placement="left"
                hasArrow
                bg="red.500"
                color="white"
                fontSize="sm"
                p={2}
                borderRadius="md"
                maxWidth="300px"
              >
                <IconButton
                  size="xl"
                  variant="ghost"
                  colorScheme="red"
                  aria-label="View errors"
                  icon={<WarningTwoIcon boxSize={5} />}
                  _hover={{ bg: "red.100" }}
                />
              </Tooltip>
            )}

            {/* Missing column warning Icon Button */}
            {observationData?.current_node?.kg_observation_data?.missing_columns && observationData?.current_node?.kg_observation_data?.missing_columns.length > 0 && (
              <Tooltip
                label={
                  <Box whiteSpace="pre-line">
                    {isColumnMissing(observationData, observationData?.current_node?.kg_observation_data!)}
                  </Box>
                }
                placement="top"
                hasArrow
              >
                <IconButton 
                  aria-label="Filters"
                  icon={<WarningTwoIcon boxSize="20px" color="yellow.500" opacity={0.7} />}
                  size="xs"
                  variant="ghost"
                  minW="20px"
                  height="20px"
                  pr={2}
                  p={0}
                />
              </Tooltip>
            )}

            {/* Copy Icon Button and Download Icon Button*/}
            <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
              <MenuButton
                as={IconButton}
                size="xs"
                color="gray.500"
                bg="transparent"
                _hover={{ bg: "gray.50", color: "gray.800" }}
                aria-label="Table options"
                icon={
                  <HStack spacing={0}>
                    <Icon as={IoCopyOutline} boxSize={5} />
                    <Icon as={FaChevronDown} boxSize={3} ml={0.5} />
                  </HStack>
                }
                onClick={(e) => e.stopPropagation()}
                mr={1}
                title="Table options"
              />
              <MenuList 
                minW="100px" 
                zIndex={1000} 
                shadow="lg" 
                border="1px solid" 
                borderColor="gray.200"
                p={1}
                position="relative"
              >
                <MenuItem 
                  icon={<Icon as={IoCopyOutline} boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    // copyTableToClipboard();
                    const rawData = extractRawDataFromNode(observationData, defaultTimeZone, quarterStartMonth);
                    copyRawTableToClipboard(rawData);
                  }}
                fontSize="sm"
                >
                  Copy to clipboard
                </MenuItem>
                <MenuItem 
                  icon={<Icon as={FaDownload} boxSize={4} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rawData = extractRawDataFromNode(observationData, defaultTimeZone, quarterStartMonth);
                    downloadRawTableAsExcel(rawData, `table_data_${observationData?.current_node?.id?.substring(0, 8) || "export"}.xlsx`);
                  }}
                  fontSize="sm"
                >
                  Download as Excel
                </MenuItem>
              </MenuList>
            </Menu>
            {/* Close Icon Button */}
            <IconButton
              icon={<Icon as={FaTimes} boxSize={4} />}
              onClick={() => handleCloseTable()}
              aria-label="Close"
              size="xs"
              color="red.400"
              colorScheme="gray"
              variant="ghost"
            />
          </HStack>
        </HStack>
        
        {/* Collapsible Filters Section */}
        <Collapse in={isFiltersExpanded} animateOpacity>
          <Box px={2} pb={2}>
            {renderFilters()}
          </Box>
        </Collapse>
      </Box>
      {/* Data Table */}
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
        p={0}
        width="100%"
        maxHeight="330px" // Match the TableContainer maxHeight
        height="auto"
        opacity={1}
        transition="all 0.3s ease-in-out"
        visibility="visible" // Add visibility transition
        mb={1}
      >
        {observationData && observationData.current_node.kg_observation_data && (
          <Box 
            flex="1" 
            overflow="auto"
            maxHeight="calc(50vh - 100px)"
            onWheel={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              target.scrollTop += e.deltaY;
            }}
          >
            <DataTableMKG 
              node={observationData}
              isChatOpen={false}
              metrics={metrics}
              treePannelLoader={false}
              showEditSuggestions={false}
              isOverlay={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DataTableComponent;